/*
  Crew Atelier — theme behaviour.
  Plain JavaScript, no dependencies. Every component is a custom element, so sections
  re-rendered by the theme editor or the Section Rendering API wire themselves up again.
  Content is always visible without this file; it only adds motion and AJAX shopping.
*/
(() => {
  'use strict';

  const theme = window.theme || (window.theme = {});
  const strings = theme.strings || {};
  const routes = theme.routes || {};
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqMobile = window.matchMedia('(max-width: 749px)');
  const mqDesktop = window.matchMedia('(min-width: 990px)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const saveData = Boolean(navigator.connection && navigator.connection.saveData);
  const motionOK = () => root.classList.contains('motion-ok') && !reduceMotion.matches;

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };

  /* ---------- Money ---------- */
  function formatMoney(cents, format) {
    const fmt = format || theme.moneyFormat || '{{amount}}';
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return frac ? grouped + decimal + frac : grouped;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return withDelimiters(value, 2);
        case 'amount_no_decimals': return withDelimiters(value, 0);
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', '');
        case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return withDelimiters(value, 2, ' ', '.');
        default: return withDelimiters(value, 2);
      }
    });
  }
  theme.formatMoney = formatMoney;

  /* ---------- Toast ---------- */
  let toastTimer;
  function toast(message) {
    const el = $('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  }
  theme.toast = toast;

  /* ---------- Scroll lock ---------- */
  let lockCount = 0;
  function lockScroll() {
    if (lockCount++ === 0) {
      const sb = window.innerWidth - root.clientWidth;
      root.style.setProperty('--scrollbar', `${sb}px`);
      root.classList.add('is-locked');
    }
  }
  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) root.classList.remove('is-locked');
  }

  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary, iframe, video[controls]';
  const focusables = (ctx) => $$(FOCUSABLE, ctx).filter((el) => el.offsetParent !== null || el === document.activeElement);

  /* ---------- Drawer (menu, cart, search, filters, quick add) ---------- */
  class CCDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = $('.drawer__panel', this);
      this.onKey = this.onKey.bind(this);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) {
          e.preventDefault();
          this.close();
        }
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    setExpanded(value) {
      $$(`[aria-controls="${this.id}"]`).forEach((btn) => {
        if (btn.hasAttribute('aria-expanded')) btn.setAttribute('aria-expanded', String(value));
      });
    }

    open(opener) {
      if (this.isOpen) return;
      $$('cc-drawer.is-open').forEach((d) => d !== this && d.close({ restoreFocus: false }));
      this.opener = opener || document.activeElement;
      this.hidden = false;
      void this.offsetWidth;
      requestAnimationFrame(() => this.classList.add('is-open'));
      lockScroll();
      this.setExpanded(true);
      document.addEventListener('keydown', this.onKey);
      const first = focusables(this.panel).find((el) => !el.matches('[data-drawer-close]')) || this.panel;
      setTimeout(() => (first || this.panel).focus({ preventScroll: true }), reduceMotion.matches ? 0 : 60);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKey);
      this.setExpanded(false);
      unlockScroll();
      const finish = () => {
        if (!this.classList.contains('is-open')) this.hidden = true;
      };
      if (reduceMotion.matches) finish();
      else setTimeout(finish, 620);
      if (restoreFocus && this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables(this.panel);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  define('cc-drawer', CCDrawer);

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    e.preventDefault();
    drawer.open(opener);
  });

  /* ---------- Announcement bar ---------- */
  class CCAnnouncements extends HTMLElement {
    connectedCallback() {
      this.items = $$('[data-announce-item]', this);
      this.index = 0;
      if (this.items.length < 2) return;
      this.interval = parseInt(this.dataset.interval, 10) || 5000;
      $('[data-announce-prev]', this)?.addEventListener('click', () => this.go(this.index - 1, true));
      $('[data-announce-next]', this)?.addEventListener('click', () => this.go(this.index + 1, true));
      this.addEventListener('pointerenter', () => (this.hovering = true));
      this.addEventListener('pointerleave', () => (this.hovering = false));
      this.addEventListener('focusin', () => (this.hovering = true));
      this.addEventListener('focusout', () => (this.hovering = false));
      if (!reduceMotion.matches) this.timer = setInterval(() => !this.hovering && !document.hidden && this.go(this.index + 1), this.interval);
      this.addEventListener('shopify:block:select', (e) => this.go(this.items.indexOf(e.target), true));
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    go(i) {
      const n = this.items.length;
      this.index = (i + n) % n;
      this.items.forEach((item, idx) => {
        const active = idx === this.index;
        item.classList.toggle('is-active', active);
        item.toggleAttribute('aria-hidden', !active);
        $$('a', item).forEach((a) => (active ? a.removeAttribute('tabindex') : a.setAttribute('tabindex', '-1')));
      });
    }
  }
  define('cc-announcements', CCAnnouncements);

  /* ---------- Header ---------- */
  class CCHeader extends HTMLElement {
    connectedCallback() {
      this.bar = $('.header__bar', this);
      this.nav = $('[data-nav]', this);
      this.lastY = window.scrollY;
      this.setHeight();
      this.ro = new ResizeObserver(() => {
        this.setHeight();
        this.checkFit();
      });
      this.ro.observe(this.bar);
      const announce = $('.section-announcement');
      if (announce) this.ro.observe(announce);
      if (this.nav) this.ro.observe(this.nav);
      this.setupOverlay();
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.setupMega();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.checkFit());
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      this.ro?.disconnect();
      root.classList.remove('has-header-overlay');
    }

    setHeight() {
      const h = Math.round(this.bar.getBoundingClientRect().height);
      if (h) root.style.setProperty('--header-h', `${h}px`);
      const announce = $('.section-announcement');
      root.style.setProperty('--announce-h', `${announce ? Math.round(announce.getBoundingClientRect().height) : 0}px`);
    }

    // Inline menu if it fits; otherwise a centred second row; otherwise the menu button.
    // CSS already picks a layout from an estimate; this only corrects it when the estimate was off.
    checkFit() {
      if (!this.nav || !mqDesktop.matches) {
        this.classList.remove('is-compact', 'is-stacked', 'is-inline');
        return;
      }
      const list = this.nav.firstElementChild;
      const inner = $('.header__inner', this);
      if (!list || !inner) return;
      const items = Array.from(list.children);
      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const natural = items.reduce((sum, li) => sum + li.getBoundingClientRect().width, 0) + gap * Math.max(0, items.length - 1);
      const innerStyle = getComputedStyle(inner);
      const width = inner.clientWidth - parseFloat(innerStyle.paddingLeft) - parseFloat(innerStyle.paddingRight);
      const colGap = parseFloat(innerStyle.columnGap) || 0;
      const logo = $('.logo', this).getBoundingClientRect().width;
      const end = $('.header__end', this).getBoundingClientRect().width;
      const centered = this.classList.contains('header--logo-center');
      const inlineSpace = centered ? (width - logo) / 2 - end - colGap * 2 : width - logo - end - colGap * 2;
      const needed = natural <= inlineSpace ? 'inline' : natural <= width ? 'stacked' : 'compact';
      const css = this.cssMode();
      this.classList.remove('is-compact', 'is-stacked', 'is-inline');
      if (needed !== css) this.classList.add(`is-${needed}`);
      this.dataset.navMode = needed;
    }

    cssMode() {
      const forced = ['is-compact', 'is-stacked', 'is-inline'].filter((c) => this.classList.contains(c));
      forced.forEach((c) => this.classList.remove(c));
      const startShown = getComputedStyle($('.header__start', this)).display !== 'none';
      const mode = startShown ? 'compact' : getComputedStyle($('.header__inner', this)).gridTemplateAreas.includes('nav') ? 'stacked' : 'inline';
      forced.forEach((c) => this.classList.add(c));
      return mode;
    }

    setupOverlay() {
      const first = $('#MainContent > .shopify-section:first-child');
      this.overlay = this.dataset.overlay === 'true' && first && first.querySelector('[data-header-overlay]');
      root.classList.toggle('has-header-overlay', Boolean(this.overlay));
    }

    onScroll() {
      const y = window.scrollY;
      const scrolled = y > 8;
      const overlayActive = this.overlay && !scrolled && !this.classList.contains('has-open-mega');
      this.classList.toggle('is-overlay', Boolean(overlayActive));
      this.classList.toggle('is-solid', !overlayActive && scrolled);
      if (this.dataset.hideOnScroll === 'true' && !this.classList.contains('has-open-mega') && !root.classList.contains('is-locked')) {
        const threshold = this.bar.offsetHeight + 120;
        const down = y > this.lastY + 4;
        const up = y < this.lastY - 4;
        if (down && y > threshold) this.classList.add('is-hidden');
        else if (up || y < threshold) this.classList.remove('is-hidden');
      }
      root.style.setProperty('--header-offset', this.classList.contains('is-hidden') ? '0px' : `${this.bar.offsetHeight}px`);
      this.lastY = y;
    }

    setupMega() {
      this.items = $$('[data-mega-item]', this);
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.items.forEach((item) => {
        const toggle = $('[data-mega-toggle]', item);
        const panel = $('[data-mega]', item);
        let t;
        toggle.addEventListener('click', () => (toggle.getAttribute('aria-expanded') === 'true' ? this.closeMega(item) : this.openMega(item)));
        item.addEventListener('pointerenter', (e) => {
          if (!canHover.matches || e.pointerType !== 'mouse') return;
          clearTimeout(t);
          t = setTimeout(() => this.openMega(item), 70);
        });
        item.addEventListener('pointerleave', (e) => {
          if (!canHover.matches || e.pointerType !== 'mouse') return;
          clearTimeout(t);
          t = setTimeout(() => this.closeMega(item), 200);
        });
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) this.closeMega(item);
        });
        panel.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            this.closeMega(item);
            toggle.focus();
          }
        });
        toggle.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeMega(item);
        });
      });
    }

    openMega(item) {
      this.items.forEach((other) => other !== item && this.closeMega(other));
      const toggle = $('[data-mega-toggle]', item);
      const panel = $('[data-mega]', item);
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      requestAnimationFrame(() => panel.classList.add('is-open'));
      this.classList.add('has-open-mega');
      this.onScroll();
    }

    closeMega(item) {
      const toggle = $('[data-mega-toggle]', item);
      const panel = $('[data-mega]', item);
      if (toggle.getAttribute('aria-expanded') !== 'true') return;
      toggle.setAttribute('aria-expanded', 'false');
      panel.classList.remove('is-open');
      setTimeout(() => {
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, reduceMotion.matches ? 0 : 450);
      if (!this.items.some((i) => $('[data-mega-toggle]', i).getAttribute('aria-expanded') === 'true')) {
        this.classList.remove('has-open-mega');
        this.onScroll();
      }
    }
  }
  define('cc-header', CCHeader);

  /* ---------- Hero video ---------- */
  class CCHero extends HTMLElement {
    connectedCallback() {
      this.videos = $$('[data-hero-video]', this);
      this.primary = $('[data-primary]', this);
      this.toggle = $('[data-hero-toggle]', this);
      this.media = $('[data-hero-media]', this);
      this.userPaused = false;
      if (!this.primary) return;

      this.toggle?.addEventListener('click', () => {
        if (this.primary.paused) {
          this.userPaused = false;
          this.load(true);
        } else {
          this.userPaused = true;
          this.pause();
        }
      });
      this.primary.addEventListener('playing', () => this.setPaused(false));
      this.primary.addEventListener('pause', () => this.setPaused(true));

      const autoplay = !reduceMotion.matches && !saveData;
      if (autoplay) this.load(false);
      else this.setPaused(true);

      this.io = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (!entry.isIntersecting) this.pause(true);
        else if (!this.userPaused && autoplay) this.play();
      }, { threshold: 0.08 });
      this.io.observe(this);

      if (motionOK()) {
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }
    }

    disconnectedCallback() {
      this.io?.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    activeVideos() {
      return this.videos.filter((v) => getComputedStyle(v).display !== 'none');
    }

    load(userInitiated) {
      this.activeVideos().forEach((v) => {
        if (!v.src) {
          const src = mqMobile.matches ? v.dataset.srcMobile || v.dataset.srcDesktop : v.dataset.srcDesktop || v.dataset.srcMobile;
          if (!src) return;
          v.muted = true;
          v.preload = 'auto';
          v.src = src;
          v.addEventListener('playing', () => v.classList.add('is-playing'));
          v.addEventListener('loadeddata', () => v.classList.add('is-ready'), { once: true });
          v.addEventListener('error', () => this.setPaused(true), { once: true });
        }
      });
      if (userInitiated || !this.userPaused) this.play();
    }

    play() {
      const vids = this.activeVideos().filter((v) => v.src);
      if (!vids.length) return;
      Promise.all(vids.map((v) => v.play()))
        .then(() => this.setPaused(false))
        .catch(() => this.setPaused(true));
    }

    pause(auto) {
      this.videos.forEach((v) => v.pause());
      if (!auto) this.setPaused(true);
    }

    isPaused() {
      return this.classList.contains('is-paused');
    }

    setPaused(paused) {
      this.classList.toggle('is-paused', paused);
      if (this.toggle) {
        this.toggle.setAttribute('aria-pressed', String(paused));
        this.toggle.setAttribute('aria-label', paused ? strings.play : strings.pause);
      }
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        const h = this.offsetHeight;
        const y = window.scrollY;
        if (y > h) return;
        this.media.style.transform = `translate3d(0, ${(y * 0.28).toFixed(1)}px, 0)`;
      });
    }
  }
  define('cc-hero', CCHero);

  /* ---------- Scroll reveals and parallax ---------- */
  const REVEAL = '[data-reveal-lines], [data-unveil], [data-reveal-group], .look__title, .spot__title, .promo__title, .plp__title';
  // Elements hidden with clip-path don't intersect, so clipped items are watched through their parent.
  const revealTargets = new WeakMap();
  let revealIO;
  function initReveals(scope = document) {
    if (!motionOK() || designMode) {
      root.classList.remove('js-reveal');
      return;
    }
    root.classList.add('js-reveal');
    if (!revealIO) {
      revealIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (revealTargets.get(entry.target) || [entry.target]).forEach((el) => el.classList.add('is-in'));
            revealIO.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -5% 0px', threshold: 0 });
    }
    $$(REVEAL, scope).forEach((el) => {
      if (el.classList.contains('is-in')) return;
      const watch = el.hasAttribute('data-unveil') ? el.parentElement : el;
      const list = revealTargets.get(watch) || [];
      if (!list.includes(el)) list.push(el);
      revealTargets.set(watch, list);
      revealIO.observe(watch);
    });
  }

  const parallaxItems = new Set();
  let parallaxIO;
  let parallaxTicking = false;
  function updateParallax() {
    parallaxTicking = false;
    const vh = window.innerHeight;
    parallaxItems.forEach((el) => {
      const rect = el.parentElement.getBoundingClientRect();
      const factor = parseFloat(el.dataset.parallax) || 0.06;
      const limit = rect.height * 0.07;
      const offset = clamp((rect.top + rect.height / 2 - vh / 2) * -factor, -limit, limit);
      el.style.setProperty('--py', `${offset.toFixed(1)}px`);
    });
  }
  function initParallax(scope = document) {
    if (!motionOK()) return;
    if (!parallaxIO) {
      parallaxIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? parallaxItems.add(e.target) : parallaxItems.delete(e.target)));
        updateParallax();
      });
      window.addEventListener('scroll', () => {
        if (!parallaxTicking && parallaxItems.size) {
          parallaxTicking = true;
          requestAnimationFrame(updateParallax);
        }
      }, { passive: true });
    }
    $$('[data-parallax]', scope).forEach((el) => parallaxIO.observe(el));
  }

  /* ---------- Look builder (signature scroll assembly) ---------- */
  class CCLook extends HTMLElement {
    connectedCallback() {
      if (!motionOK()) return;
      this.track = $('[data-look-track]', this);
      this.pieces = $$('[data-look-piece]', this);
      this.panel = $('.look__panel--stage', this);
      if (!this.track || !this.pieces.length) return;
      this.classList.add('is-live');
      this.update = this.update.bind(this);
      this.onScroll = () => {
        if (this.active && !this.ticking) {
          this.ticking = true;
          requestAnimationFrame(this.update);
        }
      };
      this.io = new IntersectionObserver(([e]) => {
        this.active = e.isIntersecting;
        if (this.active) this.update();
      });
      this.io.observe(this.track);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.panel?.addEventListener('focusin', () => {
        this.forcePanel = true;
        this.update();
      });
      this.update();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    update() {
      this.ticking = false;
      const rect = this.track.getBoundingClientRect();
      const span = Math.max(1, rect.height - window.innerHeight);
      const progress = clamp(-rect.top / span);
      const n = this.pieces.length;
      const stagger = n > 1 ? 0.12 : 0;
      this.pieces.forEach((piece, i) => {
        const local = clamp((progress * 1.25 - i * stagger) / 0.62);
        piece.style.setProperty('--p', easeOut(local).toFixed(4));
      });
      const panel = this.forcePanel ? 1 : easeOut(clamp((progress - 0.62) / 0.22));
      this.style.setProperty('--panel', panel.toFixed(3));
      this.style.setProperty('--panel-events', panel > 0.5 ? 'auto' : 'none');
      if (progress > 0.02) $('.look__title', this)?.classList.add('is-in');
    }
  }
  define('cc-look', CCLook);

  /* ---------- Video lists: reel cards and product gallery videos ---------- */
  function videoSrc(video) {
    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src;
      video.preload = 'auto';
    }
  }

  class CCReel extends HTMLElement {
    connectedCallback() {
      this.cards = $$('.reel__card', this);
      const autoplay = !reduceMotion.matches && !saveData;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          const video = $('video', card);
          if (!video) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            if (autoplay && !card.dataset.userPaused) this.play(card);
          } else {
            video.pause();
          }
        });
      }, { threshold: [0, 0.6] });
      this.cards.forEach((card) => {
        const video = $('video', card);
        if (!video) return;
        this.setState(card, true);
        video.addEventListener('playing', () => {
          video.classList.add('is-playing');
          this.setState(card, false);
        });
        video.addEventListener('pause', () => this.setState(card, true));
        this.io.observe(card);
        $('[data-reel-toggle]', card)?.addEventListener('click', () => {
          if (video.paused) {
            delete card.dataset.userPaused;
            this.play(card);
          } else {
            card.dataset.userPaused = 'true';
            video.pause();
            this.setState(card, true);
          }
        });
      });
      this.setupNav();
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }

    play(card) {
      const video = $('video', card);
      videoSrc(video);
      video.muted = true;
      video.play().catch(() => this.setState(card, true));
    }

    setState(card, paused) {
      card.classList.toggle('is-paused', paused);
      const btn = $('[data-reel-toggle]', card);
      if (btn) {
        btn.setAttribute('aria-pressed', String(!paused));
        btn.setAttribute('aria-label', paused ? strings.playItem : strings.pauseItem);
      }
    }

    setupNav() {
      const prev = $(`[data-scroll-prev][aria-controls="${this.id}"]`);
      const next = $(`[data-scroll-next][aria-controls="${this.id}"]`);
      if (!prev || !next) return;
      const step = () => Math.max(240, this.clientWidth * 0.75);
      prev.addEventListener('click', () => this.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      next.addEventListener('click', () => this.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      const sync = () => {
        prev.disabled = this.scrollLeft <= 4;
        next.disabled = this.scrollLeft + this.clientWidth >= this.scrollWidth - 4;
      };
      this.addEventListener('scroll', debounce(sync, 60), { passive: true });
      sync();
      new ResizeObserver(sync).observe(this);
    }
  }
  define('cc-reel', CCReel);

  /* ---------- Category index ---------- */
  class CCIndex extends HTMLElement {
    connectedCallback() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      let frame;
      this.addEventListener('pointermove', (e) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          this.style.setProperty('--x', `${e.clientX}px`);
          this.style.setProperty('--y', `${e.clientY}px`);
        });
      });
      $$('[data-index-link]', this).forEach((link) => {
        link.addEventListener('pointerenter', () => link.classList.add('is-hover'));
        link.addEventListener('pointerleave', () => link.classList.remove('is-hover'));
      });
    }
  }
  define('cc-index', CCIndex);

  /* ---------- Spotlight ---------- */
  class CCSpotlight extends HTMLElement {
    connectedCallback() {
      this.images = $$('[data-spot-img]', this);
      this.addEventListener('change', (e) => {
        const input = e.target.closest('input[type="radio"]');
        if (!input) return;
        const index = parseInt(input.value, 10);
        this.images.forEach((img, i) => img.classList.toggle('is-active', i === index));
        const name = $('[data-spot-name]', this);
        if (name) name.textContent = input.dataset.name;
        const link = $('[data-spot-link]', this);
        if (link && input.dataset.url) link.href = input.dataset.url;
      });
    }
  }
  define('cc-spotlight', CCSpotlight);

  /* ---------- Product cards: color swatch preview ---------- */
  // Colour dots on product cards: tap/click shows that colour's photo and keeps it;
  // hovering with a mouse previews it and returns to the chosen one on leave.
  function showCardImage(card, swatch) {
    const img = card && $('[data-card-img]', card);
    if (!img) return;
    if (img.dataset.originalSrc === undefined) {
      img.dataset.originalSrc = img.getAttribute('src') || '';
      img.dataset.originalSrcset = img.getAttribute('srcset') || '';
    }
    const src = swatch ? swatch.dataset.swap : img.dataset.originalSrc;
    const srcset = swatch ? swatch.dataset.swapSrcset || '' : img.dataset.originalSrcset;
    if (!src || img.getAttribute('src') === src) return;
    img.loading = 'eager';
    if (srcset) img.setAttribute('srcset', srcset);
    else img.removeAttribute('srcset');
    img.setAttribute('src', src);
  }
  function selectSwatch(swatch) {
    const card = swatch.closest('[data-card]');
    if (!card) return;
    $$('.card__swatch', card).forEach((s) => s.setAttribute('aria-pressed', String(s === swatch)));
    card.classList.add('is-swatched');
    card.chosenSwatch = swatch;
    showCardImage(card, swatch);
    const link = $('.card__title-link', card);
    if (link && swatch.dataset.url) $$('a[href]', card).forEach((a) => {
      if (a.matches('.card__frame, .card__title-link')) a.href = swatch.dataset.url;
    });
  }
  document.addEventListener('pointerover', (e) => {
    const swatch = e.target.closest('.card__swatch');
    if (!swatch || e.pointerType !== 'mouse') return;
    const card = swatch.closest('[data-card]');
    card.classList.add('is-swatched');
    showCardImage(card, swatch);
  });
  document.addEventListener('pointerout', (e) => {
    const list = e.target.closest('.card__swatches');
    if (!list || e.pointerType !== 'mouse' || list.contains(e.relatedTarget)) return;
    const card = list.closest('[data-card]');
    showCardImage(card, card.chosenSwatch || null);
    if (!card.chosenSwatch) card.classList.remove('is-swatched');
  });
  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('.card__swatch');
    if (!swatch) return;
    e.preventDefault();
    selectSwatch(swatch);
  });

  /* ---------- Cart ---------- */
  const cart = {
    drawer: () => document.getElementById('CartDrawer'),
    sectionIds() {
      const ids = [];
      if (this.drawer()) ids.push('cart-drawer');
      const page = $('[data-cart-page]');
      if (page && page.dataset.section) ids.push(page.dataset.section);
      return ids;
    },

    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.status) {
        const err = new Error(json.description || json.message || strings.cartError);
        err.status = res.status;
        throw err;
      }
      return json;
    },

    async change(line, quantity, source) {
      const scope = source?.closest('[data-cart]');
      scope?.classList.add('is-busy');
      try {
        const json = await this.request(`${routes.cartChange}.js`, { line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname });
        this.render(json.sections, json.item_count, source);
        this.announce(strings.cartUpdated);
      } catch (err) {
        toast(err.message);
        this.refresh();
      } finally {
        scope?.classList.remove('is-busy');
      }
    },

    async refresh() {
      const ids = this.sectionIds();
      if (!ids.length) return;
      const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
      const sections = await res.json();
      this.render(sections);
    },

    render(sections, itemCount, source) {
      if (!sections) return;
      const inDrawer = Boolean(source && source.closest('#CartDrawer'));
      const focusKey = source && {
        line: source.closest('[data-line]')?.dataset.line,
        qty: source.dataset.qtyChange,
        input: source.matches('[data-line-qty]')
      };
      const drawerHTML = sections['cart-drawer'];
      if (drawerHTML) {
        const fresh = $('[data-cart]', parseHTML(drawerHTML));
        const current = $('#CartDrawer [data-cart]');
        if (fresh && current) current.replaceWith(fresh);
        if (fresh && itemCount === undefined) itemCount = parseInt(fresh.dataset.itemCount, 10);
      }
      const page = $('[data-cart-page]');
      if (page && sections[page.dataset.section]) {
        const freshPage = $('.cart-page', parseHTML(sections[page.dataset.section]));
        const currentPage = page.closest('.cart-page');
        if (freshPage && currentPage) currentPage.replaceWith(freshPage);
      }
      if (itemCount !== undefined && !Number.isNaN(itemCount)) this.setCount(itemCount);
      if (focusKey && focusKey.line) {
        const container = inDrawer ? document.getElementById('CartDrawer') : $('.cart-page') || document;
        const line = $(`[data-line="${focusKey.line}"]`, container);
        const target = line && (focusKey.input ? $('[data-line-qty]', line) : $(`[data-qty-change="${focusKey.qty}"]`, line));
        if (target) target.focus({ preventScroll: true });
        else if (inDrawer) $('#CartDrawer .drawer__panel')?.focus({ preventScroll: true });
      }
    },

    setCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        el.classList.remove('is-bump');
        void el.offsetWidth;
        if (count > 0) el.classList.add('is-bump');
      });
    },

    announce(message) {
      $$('[data-cart-status]').forEach((el) => {
        el.textContent = '';
        setTimeout(() => (el.textContent = message), 50);
      });
    }
  };
  theme.cart = cart;

  class CCCart extends HTMLElement {
    connectedCallback() {
      const queue = new Map();
      const commit = debounce(() => {
        queue.forEach(({ qty, source }, line) => cart.change(line, qty, source));
        queue.clear();
      }, 350);

      this.addEventListener('click', (e) => {
        const step = e.target.closest('[data-qty-change]');
        const remove = e.target.closest('[data-line-remove]');
        if (step) {
          const lineEl = step.closest('[data-line]');
          const input = $('[data-line-qty]', lineEl);
          const next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(step.dataset.qtyChange, 10));
          input.value = next;
          queue.set(lineEl.dataset.line, { qty: next, source: step });
          commit();
        } else if (remove) {
          e.preventDefault();
          cart.change(remove.closest('[data-line]').dataset.line, 0, remove);
        }
      });
      this.addEventListener('change', (e) => {
        const input = e.target.closest('[data-line-qty]');
        if (input) {
          const qty = Math.max(0, parseInt(input.value, 10) || 0);
          cart.change(input.closest('[data-line]').dataset.line, qty, input);
        }
        const note = e.target.closest('[data-cart-note]');
        if (note) cart.request(`${routes.cartUpdate}.js`, { note: note.value }).catch(() => {});
      });
    }
  }
  define('cc-cart', CCCart);

  /* Quantity steppers outside the cart (product form) */
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-change]');
    if (!step || step.closest('[data-cart]')) return;
    const input = $('input', step.closest('[data-qty]'));
    if (!input) return;
    const min = parseInt(input.min, 10) || 1;
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(step.dataset.qtyChange, 10));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Product form (AJAX add to cart) ---------- */
  class CCProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.error = $('[data-form-error]', this);
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(e) {
      if (theme.cartType !== 'drawer' || !cart.drawer()) return;
      e.preventDefault();
      const buttons = $$(`[form="${this.form.id}"][name="add"], [name="add"]`, document).filter((b) => b.form === this.form);
      if (buttons.some((b) => b.disabled || b.classList.contains('is-loading'))) return;
      buttons.forEach((b) => {
        b.classList.add('is-loading');
        b.setAttribute('aria-disabled', 'true');
      });
      this.showError('');
      const data = new FormData(this.form);
      data.append('sections', 'cart-drawer');
      data.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: data
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || strings.error);
        cart.render(json.sections);
        const quick = this.closest('cc-drawer');
        if (quick && quick.id !== 'CartDrawer') quick.close({ restoreFocus: false });
        const drawer = cart.drawer();
        drawer.open(buttons[0]);
        cart.announce((strings.itemAdded || '').replace('[title]', json.product_title || json.title || ''));
      } catch (err) {
        this.showError(err.message || strings.error);
      } finally {
        buttons.forEach((b) => {
          b.classList.remove('is-loading');
          b.removeAttribute('aria-disabled');
        });
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }
  }
  define('cc-product-form', CCProductForm);

  /* ---------- Product (variant selection, gallery, sticky bar) ---------- */
  class CCProduct extends HTMLElement {
    connectedCallback() {
      const json = $('[data-product-json]', this);
      if (!json) return;
      try {
        this.product = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }
      this.isQuick = this.hasAttribute('data-quick');
      this.formId = this.dataset.formId;
      this.picker = $('[data-picker]', this);
      this.gallery = $('[data-gallery]', this);
      this.picker?.addEventListener('change', () => this.onOptionChange());
      if (this.picker) this.markAvailability(this.selectedOptions());
      this.setupSticky();
      this.setupShare();
    }

    disconnectedCallback() {
      if (this.onStickyScroll) window.removeEventListener('scroll', this.onStickyScroll);
    }

    selectedOptions() {
      return $$('fieldset[data-option-index]', this.picker).map((fs) => $('input:checked', fs)?.value);
    }

    findVariant(options) {
      return this.product.variants.find((v) => v.options.every((val, i) => val === options[i]));
    }

    onOptionChange() {
      const options = this.selectedOptions();
      let variant = this.findVariant(options);
      $$('fieldset[data-option-index]', this.picker).forEach((fs, i) => {
        const label = $('[data-option-selected]', fs);
        if (label) label.textContent = options[i] || '';
      });
      this.markAvailability(options);
      this.variant = variant;
      this.updateForm(variant);
      this.updatePrice(variant);
      if (variant) {
        this.updateMedia(variant);
        if (!this.isQuick && !designMode) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }
    }

    markAvailability(selected) {
      const fieldsets = $$('fieldset[data-option-index]', this.picker);
      fieldsets.forEach((fs, index) => {
        $$('input[type="radio"]', fs).forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const available = this.product.variants.some((v) => v.available && v.options.every((val, i) => i === index ? val === input.value : (i < index ? val === candidate[i] : true)));
          input.toggleAttribute('data-unavailable', !available);
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updateForm(variant) {
      const form = document.getElementById(this.formId);
      if (!form) return;
      const idInput = $('[data-variant-id]', form);
      if (idInput && variant) idInput.value = variant.id;
      const available = Boolean(variant && variant.available);
      const label = variant ? (available ? strings.addToCart : strings.soldOut) : strings.unavailable;
      $$(`[data-add-button], [data-sticky-add]`, document).filter((b) => b.form === form || b.closest('form') === form).forEach((btn) => {
        btn.disabled = !available;
        const l = $('[data-add-label]', btn);
        if (l) l.textContent = label;
      });
      const stickyVariant = $('[data-sticky-variant]', this);
      if (stickyVariant) stickyVariant.textContent = variant ? variant.title : '';
      const error = $('[data-form-error]', this);
      if (error) error.hidden = true;
    }

    updatePrice(variant) {
      const wrap = $('[data-price-wrap]', this);
      if (!wrap) return;
      const price = $('[data-price]', wrap);
      const saving = $('[data-saving]', wrap);
      if (!variant) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      price.classList.toggle('price--sale', Boolean(onSale));
      price.classList.toggle('price--sold-out', !variant.available);
      price.innerHTML = onSale
        ? `<span class="visually-hidden">${strings.salePrice}</span><span class="price__current">${formatMoney(variant.price)}</span><span class="visually-hidden">${strings.regularPrice}</span><s class="price__compare">${formatMoney(variant.compare_at_price)}</s>`
        : `<span class="price__current">${formatMoney(variant.price)}</span>`;
      if (saving) {
        saving.hidden = !onSale;
        saving.innerHTML = onSale ? (strings.saveAmount || '').replace('[amount]', formatMoney(variant.compare_at_price - variant.price)) : '';
      }
    }

    updateMedia(variant) {
      const mediaId = variant.featured_media && variant.featured_media.id;
      if (this.isQuick) {
        const img = $('[data-qa-img]', this);
        const src = variant.featured_image && variant.featured_image.src;
        if (img && src) {
          const base = src.split('?')[0];
          img.srcset = [360, 540, 720, 900].map((w) => `${base}?width=${w} ${w}w`).join(', ');
          img.src = `${base}?width=720`;
        }
        return;
      }
      if (mediaId && this.gallery && typeof this.gallery.goTo === 'function') this.gallery.goTo(mediaId);
    }

    setupSticky() {
      const bar = $('[data-sticky-buy]', this);
      const main = $('[data-main-buy]', this);
      if (!bar || !main) return;
      const btn = $('[data-sticky-add]', bar);
      let ticking = false;
      const update = () => {
        ticking = false;
        const show = main.getBoundingClientRect().bottom < 0;
        if (show === bar.classList.contains('is-visible')) return;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        if (btn) btn.tabIndex = show ? 0 : -1;
      };
      this.onStickyScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };
      window.addEventListener('scroll', this.onStickyScroll, { passive: true });
      update();
    }

    setupShare() {
      $$('[data-share]', this).forEach((btn) => {
        btn.addEventListener('click', async () => {
          const url = btn.dataset.url || window.location.href;
          if (navigator.share) {
            try {
              await navigator.share({ title: btn.dataset.title, url });
            } catch (e) { /* dismissed */ }
            return;
          }
          try {
            await navigator.clipboard.writeText(url);
            toast(strings.linkCopied);
          } catch (e) { /* clipboard unavailable */ }
        });
      });
    }
  }
  define('cc-product', CCProduct);

  class CCGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      if (!this.track) return;
      this.items = () => $$('.gallery__item', this.track);
      this.counter = $('[data-gallery-current]', this);
      this.dots = $$('.gallery__dot', this);
      this.track.addEventListener('scroll', debounce(() => this.syncIndex(), 50), { passive: true });
      this.lightbox = this.closest('cc-product')?.querySelector('[data-lightbox]');
      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-lightbox-open]');
        if (zoom && this.lightbox) this.openLightbox(zoom.closest('.gallery__item').dataset.mediaId);
      });
      if (this.lightbox) {
        this.lightbox.addEventListener('click', (e) => {
          if (e.target.closest('[data-lightbox-close]')) this.lightbox.close();
        });
        this.lightbox.addEventListener('close', () => {
          unlockScroll();
          this.lastZoom?.focus({ preventScroll: true });
        });
      }
      this.setupVideos();
    }

    syncIndex() {
      if (mqDesktop.matches) return;
      const index = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
      if (this.counter) this.counter.textContent = String(index + 1);
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    }

    goTo(mediaId) {
      const item = $(`.gallery__item[data-media-id="${mediaId}"]`, this.track);
      if (!item) return;
      if (mqDesktop.matches && this.classList.contains('gallery--stack')) {
        if (this.track.firstElementChild !== item) {
          this.track.prepend(item);
          if (motionOK()) item.animate([{ opacity: 0.3 }, { opacity: 1 }], { duration: 500, easing: 'ease-out' });
        }
        const top = this.getBoundingClientRect().top;
        if (top < 0) window.scrollTo({ top: window.scrollY + top - 100, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      } else {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }

    openLightbox(mediaId) {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      this.lastZoom = document.activeElement;
      lockScroll();
      this.lightbox.showModal();
      const target = $(`.lightbox__item[data-media-id="${mediaId}"]`, this.lightbox);
      const track = $('[data-lightbox-track]', this.lightbox);
      if (target && track) track.scrollLeft = target.offsetLeft;
      $('[data-lightbox-close]', this.lightbox)?.focus();
    }

    setupVideos() {
      const videos = $$('video[data-autoplay-visible]', this);
      if (!videos.length) return;
      const autoplay = !reduceMotion.matches && !saveData;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) {
            videoSrc(v);
            if (autoplay && !v.dataset.userPaused) v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.5 });
      videos.forEach((v) => {
        v.addEventListener('pause', () => {
          if (document.contains(v) && v.getBoundingClientRect().top < window.innerHeight && v.getBoundingClientRect().bottom > 0) v.dataset.userPaused = 'true';
        });
        v.addEventListener('play', () => delete v.dataset.userPaused);
        io.observe(v);
      });
    }
  }
  define('cc-gallery', CCGallery);

  /* ---------- Quick add ---------- */
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-add]');
    if (!trigger) return;
    const modal = document.getElementById('QuickAdd');
    const body = modal && $('[data-quick-add-body]', modal);
    if (!modal || !body) return;
    e.preventDefault();
    const url = trigger.dataset.quickAdd;
    body.innerHTML = '<p id="QuickAddTitle" class="visually-hidden"></p><div class="quick-add__loading" aria-hidden="true"><span></span><span></span><span></span></div>';
    modal.open(trigger);
    try {
      const sep = url.includes('?') ? '&' : '?';
      const res = await fetch(`${url}${sep}section_id=quick-add-product`);
      if (!res.ok) throw new Error(res.statusText);
      const doc = parseHTML(await res.text());
      const content = $('.qa', doc);
      if (!content) throw new Error('missing');
      body.innerHTML = '';
      body.appendChild(content);
      $('#QuickAddTitle', body)?.setAttribute('tabindex', '-1');
      $('#QuickAddTitle', body)?.focus({ preventScroll: true });
    } catch (err) {
      window.location.href = url;
    }
  });

  /* ---------- Predictive search ---------- */
  class CCSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-results]', this);
      if (!this.input || this.dataset.predictive !== 'true') return;
      this.input.addEventListener('input', debounce(() => this.search(), 260));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.closest('cc-drawer')?.addEventListener('drawer:open', () => this.input.select());
    }

    async search() {
      const q = this.input.value.trim();
      this.controller?.abort();
      if (q.length < 2) {
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      this.controller = new AbortController();
      try {
        const params = new URLSearchParams({ q, 'resources[type]': 'product,collection,query', 'resources[limit]': '6', section_id: 'predictive-search' });
        const res = await fetch(`${routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const fresh = $('[data-predictive-results]', doc);
        this.results.innerHTML = fresh ? fresh.outerHTML : '';
        this.input.setAttribute('aria-expanded', fresh ? 'true' : 'false');
        this.activeIndex = -1;
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }

    onKey(e) {
      const options = $$('[role="option"]', this.results);
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      if (e.key === 'Enter') {
        const active = options[this.activeIndex];
        if (active) {
          e.preventDefault();
          $('a', active)?.click();
        }
        return;
      }
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = ((this.activeIndex ?? -1) + dir + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.activeIndex)));
      this.input.setAttribute('aria-activedescendant', options[this.activeIndex].id);
      options[this.activeIndex].scrollIntoView({ block: 'nearest' });
    }
  }
  define('cc-search', CCSearch);

  /* ---------- Collection filters ---------- */
  class CCFacets extends HTMLElement {
    connectedCallback() {
      this.section = this.dataset.section;
      this.onChange = debounce(() => this.apply(this.formParams()), 450);
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-facets-form]')) this.onChange();
      });
      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-facets-form] input[type="number"]')) this.onChange();
      });
      this.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-facets-form]');
        if (!form) return;
        e.preventDefault();
        this.apply(this.formParams()).then(() => document.getElementById(`Facets-${this.section}`)?.close());
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link], [data-facets-clear], .pagination a');
        if (!link) return;
        e.preventDefault();
        const url = new URL(link.href, window.location.origin);
        this.apply(url.searchParams.toString(), true, !!link.closest('.pagination'));
      });
      this.onPop = () => this.apply(window.location.search.slice(1), false);
      window.addEventListener('popstate', this.onPop);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
    }

    formParams() {
      const form = $('[data-facets-form]', this);
      const data = new FormData(form);
      const params = new URLSearchParams();
      for (const [k, v] of data.entries()) if (v !== '') params.append(k, v);
      return params.toString();
    }

    async apply(query, push = true, scrollTop = false) {
      const target = $('[data-facets-target]', this);
      if (!target) return;
      target.classList.add('is-busy');
      const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
      try {
        const res = await fetch(`${url}${query ? '&' : '?'}section_id=${this.section}`);
        const doc = parseHTML(await res.text());
        const fresh = $('[data-facets-target]', doc);
        if (!fresh) throw new Error('missing');
        const active = document.activeElement;
        const activeKey = active && active.closest('[data-facets-form]') ? { id: active.id, name: active.name, value: active.value } : null;
        const body = $('.facets__body', target);
        const freshBody = $('.facets__body', fresh);
        if (body && freshBody) body.innerHTML = freshBody.innerHTML;
        Array.from(target.children).forEach((child) => {
          if (child.tagName !== 'CC-DRAWER') child.remove();
        });
        Array.from(fresh.children).forEach((child) => {
          if (child.tagName !== 'CC-DRAWER') target.appendChild(child);
        });
        if (activeKey) {
          const restore = (activeKey.id && document.getElementById(activeKey.id)) || $(`[name="${CSS.escape(activeKey.name)}"][value="${CSS.escape(activeKey.value)}"]`, target);
          restore?.focus({ preventScroll: true });
        }
        if (push) window.history.pushState({}, '', url);
        initReveals(target);
        if (scrollTop) target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      } catch (err) {
        window.location.href = url;
      } finally {
        target.classList.remove('is-busy');
      }
    }
  }
  define('cc-facets', CCFacets);

  /* ---------- Product recommendations ---------- */
  class CCRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const io = new IntersectionObserver(async ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        try {
          const res = await fetch(this.dataset.url);
          const doc = parseHTML(await res.text());
          const fresh = $('cc-recommendations', doc);
          if (fresh && fresh.children.length) {
            this.innerHTML = fresh.innerHTML;
            initReveals(this);
          } else {
            this.closest('.shopify-section')?.classList.add('is-empty');
          }
        } catch (e) { /* recommendations are optional */ }
      }, { rootMargin: '0px 0px 900px 0px' });
      io.observe(this);
    }
  }
  define('cc-recommendations', CCRecommendations);

  /* ---------- Small helpers ---------- */
  document.addEventListener('change', (e) => {
    const select = e.target.closest('[data-auto-submit]');
    if (select && select.form) select.form.submit();
  });

  function init(scope = document) {
    initReveals(scope);
    initParallax(scope);
  }

  init();
  reduceMotion.addEventListener?.('change', () => window.location.reload());

  const refreshHeader = () => {
    const header = $('cc-header');
    if (header && header.setupOverlay) {
      header.setupOverlay();
      header.onScroll();
    }
  };
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    refreshHeader();
  });
  document.addEventListener('shopify:section:unload', () => setTimeout(refreshHeader, 0));
  document.addEventListener('shopify:section:reorder', refreshHeader);
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = $('cc-drawer', e.target);
    if (drawer && e.target.id === 'shopify-section-cart-drawer') drawer.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = $('cc-drawer', e.target);
    if (drawer && drawer.isOpen) drawer.close();
  });
})();
